import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { IconSet } from 'stile-shared/src/components/2024/common/IconSet/IconSet';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import PedagogyHeader from 'stile-shared/src/components/2024/common/PedagogyHeader';
import PedagogyWithinStileCard from 'stile-shared/src/components/2024/common/PedagogyWithinStileCard';
import { SubNav, SubNavItem } from 'stile-shared/src/components/2024/common/SubNav';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

const ExamplesSection = styled<typeof Section>(Section)`
  margin-top: 0 !important;
  grid-column: span 12;

  .content {
    padding-top: 0 !important;
    margin-bottom: 80px;
    @media (max-width: 768px) {
      padding-top: 0 !important;
      margin-bottom: 69px !important;
      margin: 0 25px;
    }
  }

  position: relative;

  h6 {
    color: var(--stile-green-3);
    margin: 0;
    margin-bottom: 10px;
    grid-column: span 12;
    @media (max-width: 768px) {
      margin-bottom: 0 !important;
    }
  }

  .grid-layout {
    margin-top: 0 !important;
    grid-column: 1 / span 12;
  }

  > .grid-layout {
    align-items: center;
    margin-bottom: 150px;
    @media (max-width: 768px) {
      margin-bottom: 80px;
    }
  }
`;

export { Head } from 'templates/2024/Head';

function ExplicitInstructionPage(props: PageProps) {
  const exploreCurriculumImage = (
    <StaticImage
      src="../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Stacked documents with celestial diagram showing sun and orbiting planets"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );

  const subNavItems: SubNavItem[] = [
    {
      label: '5E Instructional Model',
      url: '/pedagogies/5e-instructional-model/',
    },
    {
      label: 'Phenomena-based Learning',
      url: '/pedagogies/phenomena-based-learning/',
    },
    {
      label: 'Three-dimensional Learning',
      url: '/pedagogies/three-dimensional-learning/',
    },
    {
      label: 'SOLO Taxonomy',
      url: '/pedagogies/solo-taxonomy/',
    },
    {
      label: 'Universal Design for Learning',
      url: '/pedagogies/universal-design/',
    },
    {
      label: 'Explicit Instruction',
      url: '/pedagogies/explicit-instruction/',
      isActive: true,
    },
    {
      label: 'Inquiry-based Learning',
      url: '/pedagogies/inquiry-based-learning/',
    },
    {
      label: 'Cognitive Load Theory',
      url: '/pedagogies/cognitive-load-theory/',
    },
    {
      label: 'Differentiated Instruction',
      url: '/pedagogies/differentiated-instruction/',
    },
  ];

  return (
    <PageLayout {...props}>
      <SubNav items={subNavItems} />

      <PedagogyHeader
        title="Explicit instruction"
        description="Explicit instruction is an approach where teachers clearly explain, demonstrate and model the learning process. Students know what to do and how to do it, and they are provided with opportunities to demonstrate their understanding."
        iconBullets={[
          {
            iconSrc: IconSet['lightonIcon0404'],
            title: 'Initially developed by',
            text: 'Siegfried Engelmann & Wes Becker, University of Oregon, 1960s.',
          },
          {
            iconSrc: IconSet['conflictIcon0505'],
            title: 'Effectiveness',
            text: (
              <>
                <Link
                  url="https://visible-learning.org/hattie-ranking-influences-effect-sizes-learning-achievement/"
                  openInNewTab
                >
                  This 2017 McKinsey research
                </Link>{' '}
                found that students performed best in PISA assessments, 26 points above the
                baseline, when students had learnt with a combination of inquiry-based learning and
                explicit instruction.
              </>
            ),
          },
          {
            iconSrc: IconSet['glossaryIcon0705'],
            title: 'Further reading',
            text: (
              <>
                <Link
                  url="https://www.nifdi.org/store/product/2-theory-of-instruction.html"
                  openInNewTab
                >
                  Theory of Instruction: Principle and Applications
                </Link>{' '}
                by Engelmann & Carnine.
              </>
            ),
          },
          {
            iconSrc: IconSet['locationIcon0704'],
            title: "Where you'll find it in Stile",
            text: 'Stile supports teachers to apply the principles of explicit teaching. Learning goals are made available to students, telling them what to do, and fully worked model answers show them how to do it. Teach Mode allows teachers to monitor student work and provide support when it is needed.',
          },
        ]}
      />

      <ExamplesSection>
        <h6>Examples within Stile</h6>
        <Grid>
          <PedagogyWithinStileCard
            id="i-do-example"
            title="I do: explicit teacher modeling"
            description="New concepts and skills are modelled and explained by the teacher, who demonstrates each step and shows the thinking process."
            url="http://stileapp.com/go/explicit-example-1"
            subtitle={
              <div>
                <em>Elements</em> lesson
                <br />
                in the Elements and Compounds - Lithium Edition unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/explicit_instruction_i_do.png"
                alt="Text: Arrange the labels to describe the properties of the elements. Image: Classroom activity with a teacher example showing bromine properties and student practice for copper, with draggable labels for state, colour, shine, conductivity, and other properties."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="we-do-example"
            title="We do: applying understanding together"
            description="Students engage in guided practice with prompts and worked examples."
            url="http://stileapp.com/go/explicit-example-2"
            subtitle={
              <div>
                <em>Elements</em> lesson
                <br />
                in the Elements and Compounds - Lithium Edition unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/explicit_instruction_we_do.png"
                alt="Text: Predict the physical properties that are common to all metals. Image: Brainstorm board with student responses such as conducts electricity, shiny appearance, and high melting points, alongside an illustration of students adding ideas together."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="you-do-example"
            title="You do: independent practice"
            description="Students have multiple opportunities to practice over time with tasks that allow them to apply and extend their knowledge."
            url="http://stileapp.com/go/explicit-example-3"
            subtitle={
              <div>
                <em>Elements</em> lesson
                <br />
                in the Elements and Compounds - Lithium Edition unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/explicit_instruction_you_do.png"
                alt="Text: Compare metals and non-metals by their common properties. Image: Split table for metals and non-metals with a photo of shiny metal pipes."
                objectFit="contain"
              />
            }
          />
        </Grid>
      </ExamplesSection>

      <ExploreCurriculum
        title="Explore our core science curriculum"
        image={exploreCurriculumImage}
        exploreCardsData={[
          {
            title: "Inside Stile's Curriculum",
            description: 'How we make the best science lessons in the known universe.',
            url: '/what-is-stile/middle-school-curriculum/',
          },
          {
            title: "What's Included",
            description:
              'A complete, comprehensive curriculum — used, loved, and perfected by thousands of teachers.',
            url: '/what-is-stile/whats-included/',
          },
          {
            title: 'Why HQIM?',
            description: 'Why High-Quality Instructional Materials make all the difference.',
            url: '/why-choose-stile/high-quality-instructional-materials/',
          },
        ]}
      />
    </PageLayout>
  );
}

export default ExplicitInstructionPage;
